<template>
  <div>
    <div class="mc-hero">
      <div class="mc-hero-icon">
        <svg
          width="34"
          height="34"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      <h1 class="mc-h1">Claim form submitted</h1>
      <p class="mc-lede">
        We've sent a confirmation SMS to <b>{{ phone || 'your number' }}</b
        >.
      </p>
      <div class="mc-ref">{{ reference || 'CLM/2026/000000' }}</div>
      <p class="mc-hint">
        Quote this reference in any call or message about the claim.
      </p>
    </div>

    <div class="mc-card mc-mt16">
      <div class="mc-cardhead"><b>What happens next</b></div>
      <div class="mc-tl">
        <div
          v-for="stage in stages"
          :key="stage.title"
          class="mc-step"
          :class="{ 'is-now': stage.now }"
        >
          <div class="mc-pin">
            <svg
              v-if="stage.now"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              stroke-width="3.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <b>{{ stage.title }}</b>
          <span>{{ stage.detail }}</span>
        </div>
      </div>
    </div>

    <div class="mc-card">
      <div class="mc-cardhead"><b>Need help?</b></div>
      <div class="mc-help-copy">
        Call APA claims on <b class="mc-help-number">0709 912 000</b> or reply
        to the SMS with your reference.
      </div>
    </div>
  </div>
</template>

<script>
// Shared terminal screen — the design's ROUTES.done is reached from the
// online-form path, the paper-upload path and the AI path alike.
export default {
  name: 'DoneStep',
  props: {
    reference: { type: String, default: '' },
    phone: { type: String, default: '' },
  },
  data() {
    return {
      stages: [
        {
          title: 'Claim received',
          detail: 'Just now — we have everything we need to start.',
          now: true,
        },
        {
          title: 'Under review',
          detail: 'Within 24 hours a claims officer checks your report.',
          now: false,
        },
        {
          title: 'Assessor assigned',
          detail: "We'll text you the assessor's name and appointment.",
          now: false,
        },
        {
          title: 'Repairs authorised',
          detail: 'Once approved, repairs begin at your chosen location.',
          now: false,
        },
      ],
    }
  },
}
</script>

<style scoped>
.mc-hero {
  text-align: center;
  padding: 26px 10px 10px;
}

.mc-hero-icon {
  width: 76px;
  height: 76px;
  margin: 0 auto 16px;
  border-radius: 50%;
  /* solid colour (pre-blended over white) instead of an rgba tint — a flat
     contrast check can't resolve alpha-over-unknown-parent and misreads the
     raw translucent value as darker than what actually renders */
  background: #e3f4ea;
  display: grid;
  place-items: center;
  /* darker than the design's #27ae60 accent — needed for contrast against
     the pale green background here */
  color: #166534;
}

.mc-h1 {
  font-size: 24px;
  line-height: 1.22;
  font-weight: 700;
  margin: 0 0 8px;
  color: #14142b;
  letter-spacing: -0.35px;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 20px;
}

.mc-ref {
  display: inline-block;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #003087;
  background: #eef3ff;
  border: 1px dashed #88b9eb;
  padding: 10px 18px;
  border-radius: 8px;
  margin: 6px 0 4px;
}

.mc-hint {
  font-size: 12.5px;
  color: #6e7191;
  margin: 6px 0 0;
}

.mc-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
}

.mc-mt16 {
  margin-top: 16px;
}

.mc-cardhead {
  margin-bottom: 10px;
}

.mc-cardhead b {
  font-size: 14.5px;
  font-weight: 700;
  color: #14142b;
}

.mc-tl {
  position: relative;
  padding-left: 34px;
  margin-top: 6px;
}

.mc-step {
  position: relative;
  padding-bottom: 22px;
}

.mc-step:last-child {
  padding-bottom: 0;
}

.mc-step::before {
  content: '';
  position: absolute;
  left: -24px;
  top: 22px;
  bottom: 0;
  width: 2px;
  background: #d9dbe9;
}

.mc-step:last-child::before {
  display: none;
}

.mc-pin {
  position: absolute;
  left: -34px;
  top: 1px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #eff0f6;
  border: 2px solid #d9dbe9;
  display: grid;
  place-items: center;
  /* darker than the usual #a0a3bd secondary text — this sits on the tinted
     #eff0f6 background, where that lighter tone fails contrast */
  color: #4e4b66;
}

.mc-step.is-now .mc-pin {
  /* darker than the design's #27ae60 accent — needed for the white
     checkmark icon to have enough contrast against the fill */
  background: #1e8449;
  border-color: #1e8449;
  color: #fff;
}

.mc-step.is-now::before {
  background: #1e8449;
}

.mc-step b {
  display: block;
  font-size: 14.5px;
  font-weight: 700;
  color: #14142b;
}

.mc-step span {
  display: block;
  font-size: 13px;
  color: #6e7191;
  margin-top: 2px;
}

.mc-help-copy {
  font-size: 13.5px;
  color: #6e7191;
  line-height: 1.6;
}

.mc-help-number {
  color: #333333;
}
</style>
